<script setup>
import { computed, watch, nextTick, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import AgentMessageBubble from './AgentMessageBubble.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const threadRef = ref(null);
const topSentinelRef = ref(null);
const currentUser = useMapGetter('getCurrentUser');

const getMessages = useFunctionGetter(
  'agentMessages/getMessages',
  computed(() => props.conversationId)
);
const getUIFlags = useFunctionGetter(
  'agentMessages/getUIFlags',
  computed(() => props.conversationId)
);
const getHasMore = useFunctionGetter(
  'agentMessages/getHasMore',
  computed(() => props.conversationId)
);

const messages = computed(() => getMessages.value);
const isFetching = computed(() => getUIFlags.value?.fetching);
const hasMore = computed(() => getHasMore.value);

const scrollToBottom = () => {
  nextTick(() => {
    if (threadRef.value) {
      threadRef.value.scrollTop = threadRef.value.scrollHeight;
    }
  });
};

// Only auto-scroll when new messages are appended, not when read_at updates
watch(
  () => messages.value.length,
  (newLen, oldLen) => {
    if (newLen > oldLen) scrollToBottom();
  }
);

// Always jump to bottom when switching conversations
watch(() => props.conversationId, scrollToBottom);

const loadOlderMessages = async () => {
  if (!hasMore.value || isFetching.value) return;
  const oldest = messages.value[0];
  if (!oldest) return;

  const el = threadRef.value;
  const prevScrollHeight = el?.scrollHeight ?? 0;

  await store.dispatch('agentMessages/fetchMessages', {
    conversationId: props.conversationId,
    beforeId: oldest.id,
  });

  // Restore scroll position so the view doesn't jump after prepend
  await nextTick();
  if (el) {
    el.scrollTop = el.scrollHeight - prevScrollHeight;
  }
};

let observer = null;

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) loadOlderMessages();
    },
    { threshold: 0.1 }
  );
  if (topSentinelRef.value) observer.observe(topSentinelRef.value);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <div
    ref="threadRef"
    class="conversation-panel flex flex-col overflow-y-auto relative h-full px-4 py-4 gap-1"
  >
    <div ref="topSentinelRef" class="h-1 shrink-0" />
    <AgentMessageBubble
      v-for="message in messages"
      :key="message.id"
      :message="message"
      :is-own="message.sender.id === currentUser?.id"
    />
    <div
      v-if="!messages.length"
      class="flex flex-1 items-center justify-center"
    >
      <p class="text-sm text-n-slate-10">{{ t('AGENT_CHAT.EMPTY_THREAD') }}</p>
    </div>
  </div>
</template>
